<template>
  <div>
    <!-- Empty State -->
    <EmptyState
      v-if="groupedItems.length === 0"
      icon="shopping"
      :title="t('app.views.kitchen.no_active')"
      :description="t('app.views.kitchen.no_active_description')"
      variant="card"
      size="md"
    />

    <!-- Grouped Items Grid -->
    <div
      v-else
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
    >
      <div
        v-for="item in groupedItems"
        :key="`${item.menu_item_id}_${item.variant_id}`"
        class="bg-white dark:bg-gray-900 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
      >
        <div class="p-4 sm:p-5 space-y-3">
          <!-- Item Header -->
          <div class="flex items-start gap-3">
            <span class="font-bold text-2xl sm:text-3xl text-indigo-600 dark:text-indigo-400 flex-shrink-0">
              {{ item.total_quantity }}x
            </span>
            <div class="flex-1 min-w-0">
              <h3 class="text-lg sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                {{ item.menu_item_name }}
              </h3>
              <p
                v-if="item.variant_name"
                class="text-sm sm:text-base text-gray-600 dark:text-gray-400 truncate"
              >
                {{ item.variant_name }}
              </p>
            </div>
          </div>

          <!-- Category Badge -->
          <span
            v-if="item.category"
            class="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase"
          >
            {{ item.category }}
          </span>

          <!-- Item Type Badge (only for takeaway items) -->
          <span
            v-if="item.item_type === 'takeaway'"
            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
          >
            PARA LLEVAR
          </span>

          <!-- Orders Breakdown -->
          <div>
            <p class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              {{ t('app.views.kitchen.grouped.from_orders') }}:
            </p>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="(orderInfo, idx) in item.orders"
                :key="idx"
                class="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium"
              >
                <span class="font-bold">{{ orderInfo.quantity }}x</span>
                <span>{{ t('app.views.kitchen.order', { id: orderInfo.order_number || orderInfo.order_id }) }}</span>
                <span
                  v-if="orderInfo.table_number"
                  class="text-xs"
                >
                  ({{ t('app.views.kitchen.table_short', { number: orderInfo.table_number }) }})
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import EmptyState from '@/components/base/EmptyState.vue';
import type { GroupedKitchenItem } from '@/utils/kitchenHelpers';

interface Props {
  groupedItems: GroupedKitchenItem[];
}

defineProps<Props>();

const { t } = useI18n();
</script>
